// One row per watchdog sweep (run_id): when it started, how many real turns
// it judged, the verdict tallies and the slow-path replay tallies.

import type { WatchdogRunSummary } from '../../services/fastpathQuality';
import { whenUtc } from './format';
import styles from '../FastpathQualityPage.module.css';

interface Props {
  runs: WatchdogRunSummary[];
  selectedId: string | null;
  onSelect: (runId: string) => void;
}

function rate(run: WatchdogRunSummary): string {
  const bad = (run.verdicts.unfaithful ?? 0) + (run.verdicts.no_answer ?? 0);
  const judged = run.checks - (run.verdicts.unjudgeable ?? 0);
  if (!judged) return '–';
  return `${Math.round((bad / judged) * 100)}%`;
}

export function WatchdogRunsTable({ runs, selectedId, onSelect }: Props) {
  return (
    <section className={styles.panel} aria-label="Watchdog sweeps">
      <div className={styles.panelHead}>
        <h2>Sweeps</h2>
        <span className={styles.meta}>newest first · click a sweep to see its checks</span>
      </div>
      {runs.length === 0 ? (
        <p className={styles.empty}>No watchdog sweep has written a check yet (daily beat, 12:40 UTC).</p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>first check</th>
                <th>run</th>
                <th className={styles.num}>checks</th>
                <th className={styles.num}>failure rate</th>
                <th className={styles.num}>faithful / unfaithful / no answer / unjudgeable</th>
                <th className={styles.num}>replay agree / disagree / failed</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr
                  key={run.run_id}
                  className={`${styles.clickable} ${run.run_id === selectedId ? styles.selectedRow : ''}`}
                  onClick={() => onSelect(run.run_id)}
                >
                  <td>{whenUtc(run.first_check_at)}</td>
                  <td><code className={styles.tag}>{run.run_id.slice(0, 8)}</code></td>
                  <td className={styles.num}>{run.checks}</td>
                  <td className={styles.num}>{rate(run)}</td>
                  <td className={styles.num}>
                    {run.verdicts.faithful ?? 0} / {run.verdicts.unfaithful ?? 0} / {run.verdicts.no_answer ?? 0} / {run.verdicts.unjudgeable ?? 0}
                  </td>
                  <td className={styles.num}>
                    {run.replays.agree ?? 0} / {run.replays.disagree ?? 0} / {run.replays.replay_failed ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
